import {
  ArrowLeftRight,
  Atom,
  BatteryCharging,
  Beaker,
  Calculator,
  CircleDot,
  Droplets,
  Filter,
  Flame,
  Fuel,
  GlassWater,
  Hammer,
  Pipette,
  Route,
  Link2,
  Network,
  FlaskConical,
  Gauge,
  Microscope,
  GitBranch,
  Layers3,
  Lightbulb,
  Magnet,
  Scale,
  ScanSearch,
  Shapes,
  Sparkles,
  Telescope,
  TestTube,
  Thermometer,
  Wind,
  Zap,
} from 'lucide-react';

import { SOLUTIONS_TOOL_IDENTITY } from './solutionsLab';
import type { StudyTool } from './studyTools';

/** Chemistry catalogue entries, kept apart so `studyTools.ts` stays under the 1000-line file limit. */
export const CHEMISTRY_STUDY_TOOLS: StudyTool[] = [
  {
    id: 'matter-change',
    subjectKey: 'chemistry',
    category: 'matter-foundations',
    name: 'Matter & Change Lab',
    description:
      'Move from Year 6 reversible changes to Year 7 mixture separation and Year 8 chemical-change evidence.',
    path: '/learn/study/subjects/chemistry/tools/matter-change',
    actionLabel: 'Open matter lab',
    icon: Layers3,
    tone: 'sunshine',
    availableFrom: 6,
  },
  {
    id: 'solutions',
    subjectKey: 'chemistry',
    category: 'matter-foundations',
    name: 'Solutions, Solubility & Concentration Lab',
    description:
      'Move from visible mixtures to solubility curves, crystallisation and quantitative dilution.',
    path: '/learn/study/subjects/chemistry/tools/solutions',
    actionLabel: 'Open solutions lab',
    icon: Beaker,
    tone: 'sky',
    availableFrom: 6,
    toolKey: SOLUTIONS_TOOL_IDENTITY.toolKey,
    toolVersion: SOLUTIONS_TOOL_IDENTITY.toolVersion,
    curriculumPolicy: SOLUTIONS_TOOL_IDENTITY.curriculumPolicy,
  },
  {
    id: 'periodic-table',
    subjectKey: 'chemistry',
    category: 'elements',
    name: 'Periodic Table Explorer',
    description:
      'A general tool for every Year: symbols and metals first, atomic structure in Year 9, trends and orbitals later.',
    path: '/learn/study/subjects/chemistry/tools/periodic-table',
    actionLabel: 'Open periodic table',
    icon: Atom,
    tone: 'sky',
    availableFrom: 8,
    general: true,
  },
  {
    // Independent full-screen lab (Airbotix-AI-Business/airbotix-chem-lab), opened at /labs/chemistry.
    id: 'chemistry-lab',
    toolVersion: '0.1.0',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Virtual Chemistry Lab',
    description:
      'Run guided experiments on a 3D lab bench or explore the free bench, then see what changed and why.',
    path: '/learn/study/subjects/chemistry/tools/chemistry-lab',
    actionLabel: 'Open Virtual Chemistry Lab',
    icon: FlaskConical,
    tone: 'mint',
    availableFrom: 6,
    general: true,
    keywords:
      'chemistry laboratory lab bench experiment reaction apparatus 3d virtual 化学 实验室 实验 反应 虚拟',
  },
  {
    id: 'molecule-bonding',
    subjectKey: 'chemistry',
    category: 'structure-bonding',
    name: 'Bond Lab · Molecule Builder',
    description:
      'Join atoms with visible single, double or triple bonds, build several molecules on one canvas and browse 119 substances.',
    path: '/learn/study/subjects/chemistry/tools/molecule-bonding',
    actionLabel: 'Open Bond Lab',
    icon: Sparkles,
    tone: 'mint',
    availableFrom: 10,
  },
  {
    id: 'equation-lab',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Chemical Equation Lab',
    description:
      'Type a skeleton equation, see how every formula is recognised and balance atoms by changing coefficients.',
    path: '/learn/study/subjects/chemistry/tools/equation-lab',
    actionLabel: 'Open equation lab',
    icon: FlaskConical,
    tone: 'coral',
    availableFrom: 9,
  },
  {
    id: 'stoichiometry-lab',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Molar Mass & Stoichiometry',
    description:
      'Break down formula mass, follow mole ratios and compare limiting reagents and yield.',
    path: '/learn/study/subjects/chemistry/tools/stoichiometry-lab',
    actionLabel: 'Open calculation lab',
    icon: Scale,
    tone: 'mint',
    availableFrom: 11,
  },
  {
    id: 'ionic-equation-lab',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Ionic Equation Lab',
    description:
      'Check atoms and charge, identify spectator ions and simplify a complete ionic equation.',
    path: '/learn/study/subjects/chemistry/tools/ionic-equation-lab',
    actionLabel: 'Open ionic lab',
    icon: Zap,
    tone: 'coral',
    availableFrom: 11,
  },
  {
    id: 'redox-lab',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Redox Lab',
    description: 'Track oxidation numbers and see which element loses or gains electrons.',
    path: '/learn/study/subjects/chemistry/tools/redox-lab',
    actionLabel: 'Open redox lab',
    icon: Zap,
    tone: 'sunshine',
    availableFrom: 11,
  },
  {
    id: 'reaction-predictor',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Reaction Predictor',
    description:
      'Match reviewed reaction patterns and see likely products without unsafe guessing.',
    path: '/learn/study/subjects/chemistry/tools/reaction-predictor',
    actionLabel: 'Open predictor',
    icon: Telescope,
    tone: 'sky',
    availableFrom: 10,
  },
  {
    id: 'reaction-rate',
    subjectKey: 'chemistry',
    category: 'rates-systems',
    name: 'Reaction Rate & Collision Lab',
    description:
      'Predict how temperature, concentration, surface area and catalysts change successful collisions, then use Maxwell–Boltzmann curves from Year 11.',
    path: '/learn/study/subjects/chemistry/tools/reaction-rate',
    actionLabel: 'Open rate lab',
    icon: Gauge,
    tone: 'sunshine',
    availableFrom: 10,
  },
  {
    id: 'ph-titration',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'pH & Virtual Titration Lab',
    description:
      'Compare acid and base strength, deliver titrant and connect particles, indicators and titration curves.',
    path: '/learn/study/subjects/chemistry/tools/ph-titration',
    actionLabel: 'Open titration lab',
    icon: Droplets,
    tone: 'sky',
    availableFrom: 10,
  },
  {
    id: 'equilibrium',
    subjectKey: 'chemistry',
    category: 'rates-systems',
    name: 'Equilibrium & Le Chatelier Lab',
    description:
      'Disturb a reversible system and predict its shift, then solve ICE-table and Ksp precipitation calculations.',
    path: '/learn/study/subjects/chemistry/tools/equilibrium',
    actionLabel: 'Open equilibrium lab',
    icon: ArrowLeftRight,
    tone: 'mint',
    availableFrom: 11,
  },
  {
    id: 'organic-builder',
    subjectKey: 'chemistry',
    category: 'organic-analysis',
    name: 'Organic Chemistry Builder',
    description:
      'Build a reviewed carbon chain with an optional methyl branch, name it, then compare structural, cis/trans and optical isomers.',
    path: '/learn/study/subjects/chemistry/tools/organic-builder',
    actionLabel: 'Open organic builder',
    icon: GitBranch,
    tone: 'coral',
    availableFrom: 11,
  },
  {
    id: 'organic-spectroscopy',
    subjectKey: 'chemistry',
    category: 'analysis-separation',
    name: 'Organic Spectroscopy Detective',
    description:
      'Combine molecular ion, IR, ¹³C NMR and ¹H NMR evidence to identify reviewed unknowns.',
    path: '/learn/study/subjects/chemistry/tools/spectroscopy',
    actionLabel: 'Open spectroscopy detective',
    icon: ScanSearch,
    tone: 'sky',
    availableFrom: 11,
  },
  {
    id: 'chromatography-lab',
    subjectKey: 'chemistry',
    category: 'analysis-separation',
    name: 'Chromatography & Mixture Analysis Lab',
    description:
      'Develop paper and TLC chromatograms, calculate Rf values, then identify and quantify components by HPLC.',
    path: '/learn/study/subjects/chemistry/tools/chromatography',
    actionLabel: 'Open chromatography lab',
    icon: FlaskConical,
    tone: 'mint',
    availableFrom: 7,
  },
  {
    id: 'particle-model',
    subjectKey: 'chemistry',
    category: 'matter-foundations',
    name: 'Particle Model & States Lab',
    description:
      'Heat and cool a substance, watch particles change state and compare diffusion and density.',
    path: '/learn/study/subjects/chemistry/tools/particle-model',
    actionLabel: 'Open particle lab',
    icon: CircleDot,
    tone: 'mint',
    availableFrom: 7,
  },
  {
    id: 'matter-classifier',
    subjectKey: 'chemistry',
    category: 'matter-foundations',
    name: 'Elements, Compounds & Mixtures Sorter',
    description:
      'Sort particle diagrams into elements, compounds and mixtures, then read chemical formulas.',
    path: '/learn/study/subjects/chemistry/tools/matter-classifier',
    actionLabel: 'Open sorter',
    icon: Shapes,
    tone: 'coral',
    availableFrom: 7,
  },
  {
    id: 'energy-calorimetry',
    subjectKey: 'chemistry',
    category: 'energy-thermo',
    name: 'Energy & Calorimetry Lab',
    description:
      'Predict exothermic or endothermic change, then calculate enthalpy, use Hess\'s law, compare fuels and test spontaneity.',
    path: '/learn/study/subjects/chemistry/tools/energy-calorimetry',
    actionLabel: 'Open energy lab',
    icon: Thermometer,
    tone: 'sunshine',
    availableFrom: 8,
  },
  {
    id: 'atomic-models',
    subjectKey: 'chemistry',
    category: 'elements',
    name: 'Atomic Models & Isotopes',
    description:
      'Follow the evidence from Dalton to Chadwick, build isotopes and calculate relative atomic mass.',
    path: '/learn/study/subjects/chemistry/tools/atomic-models',
    actionLabel: 'Open atomic models',
    icon: Atom,
    tone: 'coral',
    availableFrom: 9,
  },
  {
    id: 'electrochemical-cells',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Electrochemical Cells Lab',
    description:
      'Balance half-equations, build galvanic cells from standard potentials, predict electrolysis products and explain primary, secondary and fuel cells.',
    path: '/learn/study/subjects/chemistry/tools/electrochemical-cells',
    actionLabel: 'Open cells lab',
    icon: BatteryCharging,
    tone: 'mint',
    availableFrom: 11,
  },
  {
    id: 'gas-laws',
    subjectKey: 'chemistry',
    category: 'gases-quantities',
    name: 'Gas Laws Lab',
    description:
      'Change pressure, volume and temperature, then solve pV = nRT with clear units and conditions.',
    path: '/learn/study/subjects/chemistry/tools/gas-laws',
    actionLabel: 'Open gas laws',
    icon: Wind,
    tone: 'sky',
    availableFrom: 11,
  },
  {
    id: 'intermolecular-forces',
    subjectKey: 'chemistry',
    category: 'structure-bonding',
    name: 'Intermolecular Forces Lab',
    description:
      'Identify dispersion, dipole and hydrogen-bond forces and rank boiling points against real data.',
    path: '/learn/study/subjects/chemistry/tools/intermolecular-forces',
    actionLabel: 'Open forces lab',
    icon: Magnet,
    tone: 'sunshine',
    availableFrom: 11,
  },
  {
    id: 'instrumental-analysis',
    subjectKey: 'chemistry',
    category: 'analysis-separation',
    name: 'Instrumental Analysis Lab',
    description:
      'Identify ions with flame tests, emission lines and precipitate tests, then use calibration curves and gravimetric analysis.',
    path: '/learn/study/subjects/chemistry/tools/instrumental-analysis',
    actionLabel: 'Open analysis lab',
    icon: Microscope,
    tone: 'coral',
    availableFrom: 11,
  },
  {
    id: 'polymers-biomolecules',
    subjectKey: 'chemistry',
    category: 'organic-analysis',
    name: 'Polymers & Biomolecules Lab',
    description:
      'Build addition and condensation polymers, peptides, sugars and triglycerides, explain how soaps clean, and model enzymes, digestion and food energy.',
    path: '/learn/study/subjects/chemistry/tools/polymers-biomolecules',
    actionLabel: 'Open polymers lab',
    icon: Link2,
    tone: 'mint',
    availableFrom: 11,
  },
  {
    id: 'combustion-corrosion',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Combustion & Corrosion Lab',
    description:
      'Test what makes iron rust and fuels burn, then write combustion and rusting equations.',
    path: '/learn/study/subjects/chemistry/tools/combustion-corrosion',
    actionLabel: 'Open combustion lab',
    icon: Flame,
    tone: 'coral',
    availableFrom: 6,
  },
  {
    id: 'separation-planner',
    subjectKey: 'chemistry',
    category: 'matter-foundations',
    name: 'Separation Planner',
    description:
      'Plan the order of steps that separates a mixture, then design a water treatment process.',
    path: '/learn/study/subjects/chemistry/tools/separation-planner',
    actionLabel: 'Open separation planner',
    icon: Filter,
    tone: 'sky',
    availableFrom: 7,
  },
  {
    id: 'metals-nonmetals',
    subjectKey: 'chemistry',
    category: 'elements',
    name: 'Metals & Non-metals Lab',
    description:
      'Test conductivity, lustre and malleability to sort elements by evidence, including the exceptions.',
    path: '/learn/study/subjects/chemistry/tools/metals-nonmetals',
    actionLabel: 'Open metals lab',
    icon: Lightbulb,
    tone: 'sunshine',
    availableFrom: 8,
  },
  {
    id: 'conservation-of-mass',
    subjectKey: 'chemistry',
    category: 'reactions',
    name: 'Conservation of Mass Lab',
    description:
      'Turn word equations into balanced equations and explain mass changes in open and closed systems.',
    path: '/learn/study/subjects/chemistry/tools/conservation-of-mass',
    actionLabel: 'Open mass lab',
    icon: Scale,
    tone: 'mint',
    availableFrom: 9,
  },
  {
    id: 'acid-reactions',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Acid Reactions Lab',
    description:
      'React acids with metals, carbonates and bases, name the salt and test the gas.',
    path: '/learn/study/subjects/chemistry/tools/acid-reactions',
    actionLabel: 'Open acid reactions',
    icon: TestTube,
    tone: 'coral',
    availableFrom: 9,
  },
  {
    id: 'group-reactivity',
    subjectKey: 'chemistry',
    category: 'elements',
    name: 'Group Reactivity Lab',
    description:
      'Compare alkali metals in water and halogen displacement, and explain the trends.',
    path: '/learn/study/subjects/chemistry/tools/group-reactivity',
    actionLabel: 'Open group reactivity',
    icon: Atom,
    tone: 'sky',
    availableFrom: 10,
  },
  {
    id: 'ions-bonding',
    subjectKey: 'chemistry',
    category: 'structure-bonding',
    name: 'Ions & Bonding Lab',
    description:
      'Form ions, write ionic formulas, draw shared electrons and link structure to properties.',
    path: '/learn/study/subjects/chemistry/tools/ions-bonding',
    actionLabel: 'Open ions and bonding',
    icon: Network,
    tone: 'mint',
    availableFrom: 10,
  },
  {
    id: 'acid-base-calculations',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Acid–Base Calculations',
    description:
      'Label Brønsted–Lowry acids, bases and conjugate pairs, then calculate pH, pOH, [H₃O⁺], [OH⁻], Ka, Kb, pKa and percentage ionisation at 25 °C, including dilution, mixing and the 5 % rule.',
    path: '/learn/study/subjects/chemistry/tools/acid-base-calculations',
    actionLabel: 'Open acid–base calculations',
    icon: Calculator,
    tone: 'sky',
    availableFrom: 11,
  },
  {
    id: 'volumetric-analysis',
    subjectKey: 'chemistry',
    category: 'acids-ions',
    name: 'Volumetric Analysis',
    description:
      'Choose primary standards and make standard solutions, pick concordant titres, then solve acid–base, back and redox (permanganate, dichromate, iodine–thiosulfate) titration calculations with a mole map.',
    path: '/learn/study/subjects/chemistry/tools/volumetric-analysis',
    actionLabel: 'Open volumetric analysis',
    icon: Pipette,
    tone: 'coral',
    availableFrom: 11,
  },
  {
    id: 'water-chemistry',
    subjectKey: 'chemistry',
    category: 'water-materials',
    name: 'Water Chemistry',
    description:
      'Explain water\'s high specific heat, high enthalpy of vaporisation and density maximum with hydrogen bonding, then calculate hardness and judge water samples against Australian Drinking Water Guidelines values.',
    path: '/learn/study/subjects/chemistry/tools/water-chemistry',
    actionLabel: 'Open water chemistry',
    icon: GlassWater,
    tone: 'sky',
    availableFrom: 11,
  },
  {
    id: 'materials-chemistry',
    subjectKey: 'chemistry',
    category: 'water-materials',
    name: 'Materials Chemistry',
    description:
      'Explain metal properties with metallic bonding, classify alloys and predict heat treatment, choose extraction methods from the reactivity series, and calculate nanoscale surface-area ratios.',
    path: '/learn/study/subjects/chemistry/tools/materials-chemistry',
    actionLabel: 'Open materials chemistry',
    icon: Hammer,
    tone: 'sunshine',
    availableFrom: 11,
  },
  {
    id: 'fuels-sustainability',
    subjectKey: 'chemistry',
    category: 'energy-thermo',
    name: 'Fuels & Sustainability Lab',
    description:
      'Compare fossil fuels and biofuels by energy per gram and stoichiometric CO₂ per MJ, balance bioethanol, biodiesel, biogas and hydrogen production equations, calculate CO₂ from a balanced equation and test the carbon-neutral argument.',
    path: '/learn/study/subjects/chemistry/tools/fuels-sustainability',
    actionLabel: 'Open fuels lab',
    icon: Fuel,
    tone: 'mint',
    availableFrom: 11,
  },
  {
    id: 'synthesis-design',
    subjectKey: 'chemistry',
    category: 'organic-analysis',
    name: 'Synthesis Design Lab',
    description:
      'Plan multi-step organic routes using the Organic Builder reaction families, predict overall yield as the product of step yields, and compare routes by atom economy and green chemistry principles.',
    path: '/learn/study/subjects/chemistry/tools/synthesis-design',
    actionLabel: 'Open synthesis design',
    icon: Route,
    tone: 'coral',
    availableFrom: 11,
  },
];
